import React, { useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { Outlet } from 'react-router-dom';
import {
  FiActivity,
  FiAlertTriangle,
  FiArchive,
  FiArrowDown,
  FiArrowUp,
  FiBarChart2,
  FiBook,
  FiBox,
  FiCheckCircle,
  FiClipboard,
  FiClock,
  FiFlag,
  FiFileText,
  FiGrid,
  FiList,
  FiMap,
  FiPackage,
  FiRepeat,
  FiSliders,
  FiTool,
  FiTrendingUp,
  FiTruck,
  FiTag,
  FiLayers,
  FiUser,
  FiUsers,
  FiUpload,
} from 'react-icons/fi';
import { resolveOperationsForPath } from '../../auth/access';
import { useAuth } from '../../auth/AuthContext';
import { AuthGuard } from '../common/AuthGuard';
import { Breadcrumbs } from '../common/Breadcrumbs';
import { AppNavbar } from '../navigation/AppNavbar';
import {
  SidebarDrawer,
  SidebarNavItem,
  SidebarNavSection,
} from '../navigation/SidebarDrawer';

interface AdminNavItem {
  label: string;
  description: string;
  path: string;
  operations?: string[];
  permissions?: string[];
  icon: SidebarNavItem['icon'];
}

interface AdminNavSection extends Omit<SidebarNavSection, 'items'> {
  items: AdminNavItem[];
  operations?: string[];
  permissions?: string[];
}

const ADMIN_NAV_SECTIONS: AdminNavSection[] = [
  {
    id: 'overview',
    label: 'Visao Geral',
    icon: FiGrid,
    description: 'Acesse o painel principal.',
    items: [
      {
        label: 'Dashboard',
        description: 'Indicadores e status gerais.',
        path: '/admin',
        icon: FiGrid,
      },
    ],
  },
      {
        id: 'parametros',
        label: 'Parametros',
        icon: FiSliders,
        description: 'Tabelas base e referencias.',
        items: [
      {
        label: 'Certificacoes',
        description: 'Catalogo de certificacoes.',
        path: '/admin/aquisicao/certificacoes',
        icon: FiCheckCircle,
      },
      {
        label: 'Categorias de produto',
        description: 'Cadastro base do catalogo.',
        path: '/admin/estoque/tipos-produto',
        icon: FiList,
      },
      {
        label: 'Armazens',
        description: 'Locais de armazenamento.',
        path: '/admin/parametros/armazens',
        icon: FiArchive,
      },
      {
        label: 'Unidades de medida',
        description: 'Tabelas de unidades.',
        path: '/admin/parametros/unidades-medida',
        icon: FiList,
      },
      {
        label: 'Defeitos',
        description: 'Catalogo de ocorrencias.',
        path: '/admin/parametros/defeitos-catalogo',
        icon: FiAlertTriangle,
      },
      {
        label: 'Tipos de maquina',
        description: 'Catalogo de tipos de maquina.',
        path: '/admin/producao/tipos-maquina',
        icon: FiTool,
          },
          {
            label: 'Modelos de etiquetas',
            description: 'Configuracao de modelos de etiquetas.',
            path: '/admin/producao/etiquetas-modelos',
            icon: FiTag,
          },
      {
        label: 'UFs',
        description: 'Unidades federativas.',
        path: '/admin/pessoas/ufs',
        icon: FiFlag,
      },
      {
        label: 'Cidades',
        description: 'Municipios cadastrados.',
        path: '/admin/pessoas/cidades',
        icon: FiMap,
      },
      {
        label: 'Enderecos',
        description: 'Localizacoes para entidades.',
        path: '/admin/pessoas/enderecos',
        icon: FiMap,
      },
    ],
  },
  {
    id: 'cadastro',
    label: 'Cadastro',
    icon: FiClipboard,
    description: 'Registros que dependem dos parametros.',
    items: [
      {
        label: 'Entidades',
        description: 'Base de clientes e fornecedores.',
        path: '/admin/pessoas/entidades',
        icon: FiUsers,
      },
      {
        label: 'Produtos',
        description: 'Catalogo de produtos.',
        path: '/admin/cadastro/produtos',
        icon: FiBox,
      },
      {
        label: 'Maquinas',
        description: 'Inventario industrial.',
        path: '/admin/producao/maquinas',
        icon: FiTool,
      },
    ],
  },
  {
    id: 'producao',
    label: 'Producao',
    icon: FiPackage,
    description: 'Linhas, ordens e apontamentos.',
    items: [
      {
        label: 'Ordens de producao',
        description: 'Planejamento e execucao.',
        path: '/admin/producao/ordens-producao',
        icon: FiClipboard,
      },
      {
        label: 'Apontamentos',
        description: 'Registro de execucao em campo.',
        path: '/admin/producao/apontamentos-producao',
        icon: FiActivity,
      },
      {
        label: 'Consumo de madeira',
        description: 'Analise de materia-prima.',
        path: '/admin/producao/consumo-madeira',
        icon: FiTrendingUp,
      },
      {
        label: 'Lotes de Madeira',
        description: 'Cadastro e acompanhamento de lotes recebidos.',
        path: '/admin/producao/lotes-madeira',
        icon: FiArchive,
      },
      {
        label: 'Etiquetas',
        description: 'Geracao e impressao de etiquetas.',
        path: '/admin/producao/etiquetas',
        icon: FiTag,
      },
      {
        label: 'Fichas técnicas',
        description: 'Receitas, insumos e custos dos produtos.',
        path: '/admin/producao/fichas-tecnicas',
        icon: FiFileText,
      },
      {
        label: 'Contagens diárias',
        description: 'Registre contagens físicas para o dia.',
        path: '/admin/producao/contagens-diarias',
        icon: FiLayers,
      },
      {
        label: 'Dias de trabalho',
        description: 'Planeje o cardápio diário com fichas técnicas.',
        path: '/admin/producao/dias-trabalho',
        icon: FiClock,
      },
    ],
  },
  {
    id: 'logistica',
    label: 'Logistica',
    icon: FiTruck,
    description: 'Veiculos e transportes.',
    items: [
      {
        label: 'Veiculos',
        description: 'Frota de veiculos.',
        path: '/admin/logistica/veiculos',
        icon: FiTruck,
      },
      {
        label: 'Acoplamentos',
        description: 'Gestao de acoplamentos.',
        path: '/admin/logistica/acoplamentos',
        icon: FiClipboard,
      },
      {
        label: 'Ordens de servico',
        description: 'Manutencao de veiculos.',
        path: '/admin/logistica/ordens-servico',
        icon: FiTool,
      },
      {
        label: 'Ordens de transporte',
        description: 'Gestao de cargas.',
        path: '/admin/logistica/ordens-transporte',
        icon: FiMap,
      },
      {
        label: 'Checklists',
        description: 'Inspecoes de veiculos.',
        path: '/admin/logistica/checklists',
        icon: FiCheckCircle,
      },
    ],
  },
  {
    id: 'indicadores',
    label: 'Relatorios',
    icon: FiTrendingUp,
    description: 'KPIs e relatorios.',
    items: [
      {
        label: 'KPIs',
        description: 'Visao consolidada.',
        path: '/admin/indicadores/kpis',
        icon: FiBarChart2,
      },
      {
        label: 'Consolidação diária',
        description: 'Resumo diário das contagens físicas.',
        path: '/admin/indicadores/consolidacao-diaria',
        icon: FiLayers,
      },
      // {
      //   label: 'Metricas',
      //   description: 'Configuracao e pesos.',
      //   path: '/admin/indicadores/metricas-kpi',
      //   icon: FiSliders,
      // },
      // {
      //   label: 'Relatorios',
      //   description: 'Produtividade e desempenho.',
      //   path: '/admin/indicadores/relatorios-produtividade',
      //   icon: FiPieChart,
      // },
      {
        label: 'Estoque de produtos',
        description: 'Posicao de estoque por produto.',
        path: '/admin/estoque/estoque-produtos',
        icon: FiArchive,
      },
      {
        label: 'Inventarios',
        description: 'Controle de inventario fisico.',
        path: '/admin/estoque/inventarios',
        icon: FiLayers,
      },
    ],
  },
  {
    id: 'manutencao',
    label: 'Manutencao',
    icon: FiTool,
    description: 'Gestão de máquinas e manutenções.',
    items: [
      {
        label: 'Ordens de manutencao',
        description: 'Gestao de ordens de manutencao.',
        path: '/admin/manutencao/ordens-manutencao',
        icon: FiTool,
      },
      {
        label: 'Historico de manutencao',
        description: 'Registros de intervencoes.',
        path: '/admin/manutencao/historico-manutencao',
        icon: FiClock,
      },
    ],
  },
  {
    id: 'pessoas',
    label: 'Pessoas',
    icon: FiUsers,
    description: 'Gestao de equipes e treinamentos.',
    items: [
      {
        label: 'Operadores',
        description: 'Perfis e alocacoes.',
        path: '/admin/pessoas/operadores',
        icon: FiUser,
      },
      {
        label: 'Treinamentos',
        description: 'Capacitacoes e agendas.',
        path: '/admin/pessoas/treinamentos',
        icon: FiBook,
      },
      {
        label: 'Produtividade',
        description: 'Indicadores por operador.',
        path: '/admin/pessoas/produtividade-operador',
        icon: FiActivity,
      },
    ],
  },
  {
    id: 'imports',
    label: 'Importacoes',
    icon: FiUpload,
    description: 'Importacao em lote via CSV/Excel.',
    items: [
      {
        label: 'Importar Produtos',
        description: 'Importacao em lote de produtos.',
        path: '/admin/imports/produtos',
        icon: FiBox,
      },
      {
        label: 'Importar Ordens de Producao',
        description: 'Importacao em lote de ordens.',
        path: '/admin/imports/ordens-producao',
        icon: FiPackage,
      },
    ],
  },
];

export const AdminLayout: React.FC = () => {
  const { isAuthorized } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const navigationSections = useMemo<SidebarNavSection[]>(
    () =>
      ADMIN_NAV_SECTIONS.map((section) => {
        const allowedItems = section.items.filter((item) => {
          const operations = item.operations ?? resolveOperationsForPath(item.path);
          return isAuthorized({ operations, permissions: item.permissions });
        });

        const sectionOperations =
          section.operations ??
          resolveOperationsForPath(section.items[0]?.path ?? '/admin');

        if (
          !isAuthorized({ operations: sectionOperations, permissions: section.permissions }) ||
          allowedItems.length === 0
        ) {
          return undefined;
        }

        return {
          id: section.id,
          label: section.label,
          description: section.description,
          icon: section.icon,
          items: allowedItems,
        };
      }).filter(Boolean) as SidebarNavSection[],
    [isAuthorized],
  );

  return (
    <AuthGuard>
      <AppNavbar
        mobileNavSections={navigationSections.map((section) => ({
          id: section.id,
          label: section.label,
          items: section.items.map((item) => ({
            path: item.path,
            label: item.label,
          })),
        }))}
      />
      <main className="admin-shell">
        <div className="d-none d-lg-block">
          <SidebarDrawer
            sections={navigationSections}
            collapsed={collapsed}
            onToggle={() => setCollapsed((state) => !state)}
          />
        </div>
        <section className="admin-shell__content">
          <Container fluid className="py-4 px-4">
            <Breadcrumbs />
            <Outlet />
          </Container>
        </section>
      </main>
    </AuthGuard>
  );
};
